import React, { useState, useEffect } from 'react';
import { schools } from '../../../../libs/schoolData';
import styles from './styles.module.scss';

interface SchoolFilterSidebarProps {
  schoolName: string;
  onChange: (selectedFaculties: string[], selectedDepartments: string[]) => void;
}

const SchoolFilterSidebar: React.FC<SchoolFilterSidebarProps> = ({ schoolName, onChange }) => {
  const [expandedFaculties, setExpandedFaculties] = useState<Set<string>>(new Set());
  const [selectedFaculties, setSelectedFaculties] = useState<Set<string>>(new Set());
  const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set());

  const school = schools.find((s) => s.name === schoolName);

  // 選択状態が変わったら親に通知
  useEffect(() => {
    onChange(Array.from(selectedFaculties), Array.from(selectedDepartments));
  }, [selectedFaculties, selectedDepartments, onChange]);

  if (!school) return null;

  const toggleFaculty = (facultyKey: string) => {
    const newExpanded = new Set(expandedFaculties);
    if (newExpanded.has(facultyKey)) {
      newExpanded.delete(facultyKey);
    } else {
      newExpanded.add(facultyKey);
    }
    setExpandedFaculties(newExpanded);
  };

  const handleFacultyCheck = (facultyName: string) => {
    const facultyKey = `${schoolName}-${facultyName}`;
    const newSelectedFaculties = new Set(selectedFaculties);
    const newSelectedDepartments = new Set(selectedDepartments);

    if (newSelectedFaculties.has(facultyKey)) {
      // 学部のチェックを外す → 配下の学科も全て外す
      newSelectedFaculties.delete(facultyKey);
      const faculty = school.faculties.find((f) => f.name === facultyName);
      faculty?.departments.forEach((dept) => {
        newSelectedDepartments.delete(`${facultyKey}-${dept.name}`);
      });
    } else {
      // 学部にチェックを入れる → 配下の学科も全てチェック
      newSelectedFaculties.add(facultyKey);
      const faculty = school.faculties.find((f) => f.name === facultyName);
      faculty?.departments.forEach((dept) => {
        newSelectedDepartments.add(`${facultyKey}-${dept.name}`);
      });
    }

    setSelectedFaculties(newSelectedFaculties);
    setSelectedDepartments(newSelectedDepartments);
  };

  const handleDepartmentCheck = (facultyName: string, departmentName: string) => {
    const facultyKey = `${schoolName}-${facultyName}`;
    const departmentKey = `${facultyKey}-${departmentName}`;
    const newSelectedDepartments = new Set(selectedDepartments);
    const newSelectedFaculties = new Set(selectedFaculties);

    if (newSelectedDepartments.has(departmentKey)) {
      newSelectedDepartments.delete(departmentKey);
      // 配下の学科が全て外れた場合、学部のチェックも外す
      const faculty = school.faculties.find((f) => f.name === facultyName);
      const allDepartmentsUnchecked = faculty?.departments.every(
        (dept) => !newSelectedDepartments.has(`${facultyKey}-${dept.name}`),
      );
      if (allDepartmentsUnchecked) {
        newSelectedFaculties.delete(facultyKey);
      }
    } else {
      newSelectedDepartments.add(departmentKey);
      // 配下の学科が全てチェックされた場合、学部もチェック
      const faculty = school.faculties.find((f) => f.name === facultyName);
      const allDepartmentsChecked = faculty?.departments.every((dept) =>
        newSelectedDepartments.has(`${facultyKey}-${dept.name}`),
      );
      if (allDepartmentsChecked) {
        newSelectedFaculties.add(facultyKey);
      }
    }

    setSelectedDepartments(newSelectedDepartments);
    setSelectedFaculties(newSelectedFaculties);
  };

  const handleClearAll = () => {
    setSelectedFaculties(new Set());
    setSelectedDepartments(new Set());
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebar__header}>
        <h3 className={styles.sidebar__title}>絞り込み</h3>
        {(selectedFaculties.size > 0 || selectedDepartments.size > 0) && (
          <button className={styles.sidebar__clearButton} onClick={handleClearAll}>
            クリア
          </button>
        )}
      </div>

      <div className={styles.sidebar__content}>
        {school.faculties.map((faculty) => {
          const facultyKey = `${schoolName}-${faculty.name}`;
          const isFacultyExpanded = expandedFaculties.has(facultyKey);
          const isFacultyChecked = selectedFaculties.has(facultyKey);
          const hasDepartments = faculty.departments && faculty.departments.length > 0;

          return (
            <div key={facultyKey} className={styles.facultyItem}>
              <div className={styles.facultyHeader}>
                <div className={styles.facultyContent}>
                  <label className={styles.checkboxLabel} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={isFacultyChecked}
                      onChange={() => handleFacultyCheck(faculty.name)}
                    />
                    <span className={styles.checkboxCustom} />
                  </label>
                  <span
                    className={styles.facultyName}
                    onClick={() => hasDepartments && toggleFaculty(facultyKey)}
                  >
                    {faculty.name}
                  </span>
                </div>
                {hasDepartments && (
                  <button
                    className={styles.expandButton}
                    onClick={() => toggleFaculty(facultyKey)}
                    aria-expanded={isFacultyExpanded}
                  >
                    <svg
                      className={`${styles.icon} ${isFacultyExpanded ? styles.iconExpanded : ''}`}
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        x1="10"
                        y1="5"
                        x2="10"
                        y2="15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        className={styles.iconVertical}
                      />
                      <line
                        x1="5"
                        y1="10"
                        x2="15"
                        y2="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        className={styles.iconHorizontal}
                      />
                    </svg>
                  </button>
                )}
              </div>

              {hasDepartments && (
                <div
                  className={`${styles.departmentList} ${isFacultyExpanded ? styles.expanded : ''}`}
                >
                  {faculty.departments.map((department) => {
                    const departmentKey = `${facultyKey}-${department.name}`;
                    const isDepartmentChecked = selectedDepartments.has(departmentKey);

                    return (
                      <div key={departmentKey} className={styles.departmentItem}>
                        <label className={styles.departmentCheckbox}>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={isDepartmentChecked}
                            onChange={() => handleDepartmentCheck(faculty.name, department.name)}
                          />
                          <span className={styles.checkboxCustom} />
                        </label>
                        <span className={styles.departmentName}>{department.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default SchoolFilterSidebar;
